/**
 * Canonical cost taxonomy (single source of truth for the frontend).
 *
 * These constants mirror backend/enums.py COST_TYPE_DEFS and COST_BUCKET_LABELS
 * exactly. The /admin/cost-taxonomy endpoint serves the same data for runtime
 * verification. Every UI surface that displays cost type names, bucket labels,
 * or bucket groupings MUST import from this module, never use inline strings.
 *
 * Invariants (enforced by recurrence guards in the test suite):
 *   - cleaning_fee is guest-charged REVENUE, never a cost
 *   - management_fee_vat is pass-through, never a cost
 *   - management_commission is the external manager's cut (management_fees bucket)
 *   - manager_cleaning_per_stay is the real cleaning cost (operational bucket)
 *   - mortgage_principal, travaux, agency_fees are cash-only, never P&L
 */

// Level 1 bucket display labels (identical on every surface).
export const COST_BUCKET_LABELS = {
  ota_commission: 'OTA Commission',
  management_fees: 'Management Fees',
  operational: 'Operational',
  financing: 'Rent / Mortgage',
  investment: 'Investment',
  unclassified: 'Unclassified',
};

// P&L waterfall subtotal and result labels (not cost buckets, but used
// in every waterfall rendering surface alongside COST_BUCKET_LABELS).
export const PNL_SUBTOTAL_LABELS = {
  net_revenue: 'Net Revenue',
  net_profit: 'Net Profit',
};

// Operational subgroup display labels (Level 2, replacing the single
// "Operational" column on P&L surfaces).
export const OPERATIONAL_SUBGROUP_LABELS = {
  cleaning: 'Cleaning',
  repairs: 'Repairs',
  other_operational: 'Other',
};

// Abbreviated bucket labels for tight column headers.
export const COST_BUCKET_LABELS_SHORT = {
  ota_commission: 'OTA Comm.',
  management_fees: 'Mgmt Fees',
  operational: 'Operational',
  financing: 'Rent / Mtg.',
  investment: 'Investment',
  unclassified: 'Unclassified',
};

// Abbreviated subtotal labels for tight headers.
export const PNL_SUBTOTAL_LABELS_SHORT = {
  net_revenue: 'Net Rev.',
  net_profit: 'Net Profit',
};

// Full cost type registry. Each entry defines:
//   code, display_label, bucket, subgroup, pnl_eligible, cash_only, applies_to
export const COST_TYPE_DEFS = [
  { code: 'rent', display_label: 'Rent', bucket: 'financing', subgroup: null, pnl_eligible: true, cash_only: false, applies_to: ['leased'], is_percentage: false },
  { code: 'mortgage_interest', display_label: 'Mortgage Interest', bucket: 'financing', subgroup: null, pnl_eligible: true, cash_only: false, applies_to: ['owned'], is_percentage: false },
  { code: 'mortgage_principal', display_label: 'Mortgage Principal', bucket: 'financing', subgroup: null, pnl_eligible: true, cash_only: false, applies_to: ['owned'], is_percentage: false },
  { code: 'deposit', display_label: 'Deposit', bucket: 'investment', subgroup: null, pnl_eligible: false, cash_only: true, applies_to: ['leased'], is_percentage: false },
  { code: 'utilities', display_label: 'Utilities', bucket: 'operational', subgroup: 'utilities', pnl_eligible: true, cash_only: false, applies_to: null, is_percentage: false },
  { code: 'council_tax', display_label: 'Council Tax', bucket: 'operational', subgroup: 'taxes_charges', pnl_eligible: true, cash_only: false, applies_to: ['leased'], is_percentage: false },
  { code: 'taxe_fonciere', display_label: 'Taxe Fonciere', bucket: 'operational', subgroup: 'taxes_charges', pnl_eligible: true, cash_only: false, applies_to: ['owned'], is_percentage: false },
  { code: 'insurance', display_label: 'Insurance', bucket: 'operational', subgroup: 'insurance', pnl_eligible: true, cash_only: false, applies_to: null, is_percentage: false },
  { code: 'service_charge', display_label: 'Service Charge', bucket: 'operational', subgroup: 'taxes_charges', pnl_eligible: true, cash_only: false, applies_to: null, is_percentage: false },
  { code: 'copropriete', display_label: 'Copropriete', bucket: 'operational', subgroup: 'taxes_charges', pnl_eligible: true, cash_only: false, applies_to: ['owned'], is_percentage: false },
  { code: 'maintenance', display_label: 'Maintenance', bucket: 'operational', subgroup: 'maintenance', pnl_eligible: true, cash_only: false, applies_to: null, is_percentage: false },
  { code: 'management_commission', display_label: 'Manager Commission', bucket: 'management_fees', subgroup: null, pnl_eligible: true, cash_only: false, applies_to: null, is_percentage: true },
  { code: 'manager_cleaning_per_stay', display_label: 'Manager Cleaning (per stay)', bucket: 'operational', subgroup: 'cleaning', pnl_eligible: true, cash_only: false, applies_to: null, is_percentage: false },
  { code: 'travaux', display_label: 'Travaux', bucket: 'investment', subgroup: null, pnl_eligible: false, cash_only: true, applies_to: null, is_percentage: false },
  { code: 'agency_fees', display_label: 'Agency Fees', bucket: 'investment', subgroup: null, pnl_eligible: false, cash_only: true, applies_to: ['owned'], is_percentage: false },
  { code: 'other_statement_expense', display_label: 'Other Statement Expenses', bucket: 'operational', subgroup: 'other', pnl_eligible: true, cash_only: false, applies_to: null, is_percentage: false },
  { code: 'other', display_label: 'Other', bucket: 'operational', subgroup: 'other', pnl_eligible: true, cash_only: false, applies_to: null, is_percentage: false },
];

// Index by code for O(1) lookup.
const _BY_CODE = Object.fromEntries(COST_TYPE_DEFS.map(d => [d.code, d]));

/**
 * Get the display label for a cost type code.
 * Returns the code itself if unknown (never swallows unknowns).
 */
export function costTypeLabel(code) {
  return _BY_CODE[code]?.display_label ?? code;
}

/**
 * Get the Level 1 bucket key for a cost type code.
 * Unknown codes return 'unclassified'.
 */
export function costTypeBucket(code) {
  return _BY_CODE[code]?.bucket ?? 'unclassified';
}

/**
 * Get the full definition for a cost type code, or null.
 */
export function getCostTypeDef(code) {
  return _BY_CODE[code] ?? null;
}

/**
 * Get the display label for a bucket key.
 */
export function bucketLabel(bucketKey) {
  return COST_BUCKET_LABELS[bucketKey] ?? bucketKey;
}

/**
 * Get the short display label for a bucket key.
 */
export function bucketLabelShort(bucketKey) {
  return COST_BUCKET_LABELS_SHORT[bucketKey] ?? bucketKey;
}

// Non-P&L cost type codes (cash flow only).
export const NON_PNL_CODES = new Set(
  COST_TYPE_DEFS.filter(d => !d.pnl_eligible).map(d => d.code)
);

// Percentage-based cost types (use rate + basis instead of flat amount).
// Derived from registry is_percentage field.
export const PERCENTAGE_CODES = new Set(
  COST_TYPE_DEFS.filter(d => d.is_percentage).map(d => d.code)
);

/**
 * Build the COST_TYPES array for PropertyCostEditor dropdowns.
 * Each entry: { value, label, tenure }.
 * tenure is null if applies_to both, otherwise the single tenure string.
 */
export function buildCostTypeOptions() {
  return COST_TYPE_DEFS.map(d => ({
    value: d.code,
    label: PERCENTAGE_CODES.has(d.code)
      ? `${d.display_label} (%)`
      : d.display_label,
    tenure: d.applies_to && d.applies_to.length === 1 ? d.applies_to[0] : null,
  }));
}
